import { SharedFieldsService } from "@omnia/types"
import { CamelCaseObjectKeys } from "../../../../shared/case"
import { HoldingUserPermissions } from "../../../../shared/permissions/types"
import { ApiUserPermissions } from "../../../../shared/permissions/user/api"
import { createErrorHandler } from "../../../../shared/react/error"
import { IOmniaSession } from "../../../../shared/types"
import { RoleId } from "../../../../shared/user"
import { DefaultPermissions } from "../../defaults/defaults"
import { Cropping, CroppingPermission, HoldingRole, InitialAccessLevel, NewInvite, UserUpdate } from "./types"

export type DateString = string

export interface ApiUser {

    userId: number
    self: boolean

    firstName: string
    lastName: string
    email: string

    /** User role */
    userRole: RoleId

    /** User permissions */
    userPermissions: CamelCaseObjectKeys<ApiUserPermissions>

    /** Holding role (owner/member) */
    holdingRole: HoldingRole

    /** (Holding x user) permissions */
    holdingPermissions: HoldingUserPermissions

    /** Cropping access permissions */
    croppingPermissions: CroppingPermission[]

}

export interface ApiInvite {
    email: string
    accessLevel: InitialAccessLevel
    issuedOn: DateString
    lastSent: DateString
    acceptedOn: DateString
}

interface ApiData {
    users: ApiUser[]
    invites: ApiInvite[]
    defaultPermissions: DefaultPermissions
    cropping: Cropping[]
}

interface Invites {
    invites: ApiData["invites"]
}

export class HoldingPermissionsApi {

    constructor(
        readonly session: IOmniaSession,
        readonly fieldSvc: SharedFieldsService,
        private readonly http = session.http,
        readonly errorHandler = createErrorHandler(() => session),
        readonly currentUser = {
            userId: session.currentUser.Id,
            permissions: session.permissions
        },
        readonly enableRestrictedMembers = true
    ) {
    }

    async fetchApiData() {

        const { http, session: { yearId: year } } = this

        const cropping = await this.getCropping()

        const url = `/api/holdings/v1/permissions?year=${year}`

        const data = (await http.get<ApiData>(url)).data

        return {
            ...data,
            cropping
        }

    }

    private async getCropping() {

        const { fieldSvc } = this

        await fieldSvc.ensureInit()

        const cropping: Cropping[] = fieldSvc.cropping
            .filter(c => c.currentYear && c.visible)
            .map(c => {

                const { croppingId, description, area, fieldId, fieldName, fieldGroup, cropId, cropDescription } = c

                return {
                    croppingId,
                    description: c.getCroppingDescription(true, true, true),
                    fieldId,
                    fieldName,
                    fieldGroup,
                    area,
                    cropId,
                    cropName: c.getCropName(),
                    varietyName: c.getVarietyName(),
                    cropDescription
                }
            })

        return cropping

    }

    async updateUsers(users: UserUpdate[]) {

        const { session: { yearId: year }, http } = this

        interface Result {
            users: ApiData["users"]
        }

        const url = "/api/holdings/v1/permissions"

        const request = {
            year,
            users
        }

        const result = (await http.post<Result>(url, request)).data

        return result?.users ?? []

    }

    async switchToRestrictedMember() {

        interface Result {
            users: ApiData["users"]
        }

        const url = "/api/holdings/v1/permissions/role/restricted"

        await this.http.post<Result>(url)

        await this.session.logout()

    }

    async sendNewInvite(invite: NewInvite) {
        const url = `/api/holdings/v1/invites/send`
        return this.sendAndUpdate(() => this.http.post(url, invite))
    }

    cancelInvite(email: string) {
        const url = `/api/holdings/v1/invites/cancel?email=${encodeURIComponent(email)}`
        return this.sendAndUpdate(() => this.http.delete(url))
    }

    async sendAndUpdate(send: () => PromiseLike<{ data: Invites }>) {
        const result = (await send()).data
        return result?.invites ?? []
    }

}